import * as React from 'react';
import { DownloadSimpleIcon, XIcon, DotsSixIcon, InfoIcon, ListMagnifyingGlassIcon, CopyIcon, FileCloudIcon, PlusSquareIcon, MinusSquareIcon, ListPlusIcon, CaretLeftIcon, CaretRightIcon, BookmarksSimpleIcon, CheckIcon } from "@phosphor-icons/react";
import { Popover, PopoverTrigger, PopoverContent } from "@heroui/popover";
import { handleDownload, getMediaType, isRestrictedFileType, extractPathAfterBulk } from '@/shared/functions';
import { Tooltip } from "@heroui/tooltip";
import { useEffect, useRef, useState, useContext } from 'react';
import { SourceObject, MediaObject, MediaType, SourceInfo, EvidenceObject } from '@/shared/types';
import { useDebounce } from 'use-debounce';
import { UsableMediaTypes } from '@/shared/constants';
import { motion } from "framer-motion";
import MediaDisplay from '../ui/mediaDisplay';
import { useToolsDrawer } from '../tools-drawer';
import deepEqual from 'deep-equal';
import { FetchWithVersionCheck, formatTimeIndex } from '@/shared/functions';
import path from 'path';
import { AuthContext, FullScreenContext, FeaturesContext } from '@/app/contexts';
import { useMediaSourceShare } from '../mediaSourceShare';
import { Spinner } from '@heroui/spinner';
import { ProvenanceIcon, isStatus, getStatusColor, getStatusDescription } from '../question-bar';
import { getCurrentSourceInfo } from '@/shared/clientFunctions';
const SOURCES_LIST_LIMIT = 100;

// Add animation variants
const itemVariants = {
  dragging: {
    scale: 1.02,
    boxShadow: "0px 5px 10px rgba(0,0,0,0.2)",
    cursor: "grabbing",
    zIndex: 1,
    transition: {
      duration: 0.2
    }
  },
  normal: {
    scale: 1,
    boxShadow: "none",
    zIndex: 0,
    transition: {
      duration: 0.2
    }
  }
};

/** Same key as deduplication so enrichment can be looked up per list row (URL + page/time). */
const getSourceIdentityKey = (source: SourceObject): string => {
  const context = {
    ...(source?.sourceContext || {}),
    ...((source as any)?.mediaContext || {})
  };
  const pageNumber = context?.pageNumber || source.pageNumber;
  const startTime = context?.startTime;
  const endTime = context?.endTime;
  const url = source.sourceUrl?.trim() || (source as any).mediaUrl?.trim() || '';
  const mediaType = getMediaType(source, source.sourceInfo);
  const isMediaFile = mediaType === 'audio' || mediaType === 'video';
  const hasPageNumber = pageNumber !== undefined && pageNumber !== null && pageNumber > 0;
  const previewObjectUrl = source.sourceInfo?.previewObjectUrl || '';
  const previewFilePath = source.previewFilePath || '';
  const hasPreviewFilePath = previewFilePath !== undefined && previewFilePath !== null && previewFilePath !== '';
  const hasPreviewObjectUrl = previewObjectUrl !== undefined && previewObjectUrl !== null && previewObjectUrl !== '';
  const previewUrl = hasPreviewObjectUrl ? previewObjectUrl : hasPreviewFilePath ? previewFilePath : '';
  const parts: string[] = [url];
  if (previewUrl !== '') parts.push(previewUrl);
  if (isMediaFile) {
    if (startTime != null && startTime !== '') parts.push(String(startTime));
    if (endTime != null && endTime !== '') parts.push(String(endTime));
    return parts.join('-');
  }
  if (hasPageNumber) {
    parts.push(String(pageNumber));
    return parts.join('-');
  }
  if (previewUrl !== '') {
    return parts.join('-');
  }
  return url;
};
const deduplicateSources = (sources: SourceObject[]): SourceObject[] => {
  const seen = new Map<string, SourceObject>();
  if (!sources || sources.length === 0) return [];
  sources?.forEach(source => {
    const key = getSourceIdentityKey(source);
    if (!seen.has(key)) {
      seen.set(key, source);
    }
  });
  return Array.from(seen.values());
};
const resolveUnifiedSourcePageRef = (source: SourceObject) => source.sourceContext?.pageNumber ?? source.sourceContext?.page ?? source.pageNumber;

/** Audio/video: start/end only. Other types: page only (never time indices). */
function formatUnifiedSourceListAnnotation(sourceObject: SourceObject): string {
  const mt = getMediaType(sourceObject, sourceObject.sourceInfo);
  if (mt === 'audio' || mt === 'video') {
    const parts: string[] = [];
    if (sourceObject.sourceContext?.startTime) parts.push(`(Start: ${sourceObject.sourceContext.startTime})`);
    if (sourceObject.sourceContext?.endTime) parts.push(`(End: ${sourceObject.sourceContext.endTime})`);
    return parts.join(' ');
  }
  const pageRef = resolveUnifiedSourcePageRef(sourceObject);
  return pageRef ? `(Page ${pageRef})` : '';
}
const getValidSources = (sources: SourceObject[]): SourceObject[] => {
  return sources?.filter(source => {
    const filename = (source.truncatedFilePath || source.sourceUrl || '').split('/').pop() || 'Unknown';
    return source.sourceUrl && source.sourceUrl.trim() !== "" && source.sourceUrl !== "Unknown" && filename !== "Unknown";
  });
};
const isMediaSource = (source: SourceObject, sourceInfo: SourceInfo | null | undefined): boolean => {
  const mediaType = getMediaType(source, sourceInfo);
  return UsableMediaTypes.includes(mediaType);
};
const isSourceInMedia = (source: SourceObject, mediaValue: MediaObject[]): boolean => {
  if (!mediaValue) return false;
  let sourcePage = source.sourceContext?.pageNumber || source.pageNumber;
  const sourceStartTime = source.sourceContext?.startTime;
  return mediaValue?.some(media => {
    if (getMediaType(source, source.sourceInfo) === 'pdf') {
      if (!sourcePage) {
        sourcePage = 1;
      }
    }
    const mediaPage = media.mediaContext?.pageNumber || media.mediaContext?.page;
    const mediaStartTime = media.mediaContext?.startTime;
    // Match if URLs match AND either:
    // 1. Both have no page numbers and no start times
    // 2. Both have the same page number and no start times
    // 3. Both have the same start time (regardless of page numbers)
    const urlMatch = media.mediaSrc === source.sourceUrl || media.mediaUrl === source.sourceUrl;
    const pageMatch = !sourcePage && !mediaPage || sourcePage && mediaPage && sourcePage === mediaPage || sourcePage === 1 && !mediaPage;
    const timeMatch = !sourceStartTime && !mediaStartTime || sourceStartTime && mediaStartTime && sourceStartTime === mediaStartTime;
    const previewUrlMatch = !source.previewFilePath && !media.previewFilePath || source.previewFilePath && media.previewFilePath && source.previewFilePath === media.previewFilePath;
    return urlMatch && pageMatch && timeMatch && previewUrlMatch;
  });
};
interface UnifiedSourcesProps {
  value: SourceObject[];
  mediaValue: MediaObject[];
  slide?: number;
  column?: string;
  isInModal?: boolean;
  isInPresentationView?: boolean;
  editingEnabled: boolean;
  caseId?: string;
  handleUnifiedSourcesChanges?: (sources: SourceObject[], media: MediaObject[]) => void;
  sourcesFromChat?: SourceObject[];
  mediaSourcesFromChat?: MediaObject[];
  isTitleSlide?: boolean;
}
const UnifiedSources = (props: UnifiedSourcesProps) => {
  const {
    value,
    mediaValue,
    isInModal,
    isInPresentationView,
    editingEnabled,
    caseId,
    handleUnifiedSourcesChanges,
    sourcesFromChat,
    mediaSourcesFromChat,
    isTitleSlide
  } = props;
  const [newSources, setNewSources] = useState<SourceObject[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [sourcesForResults, setSourcesForResults] = useState<SourceObject[]>([]);
  const [searchTerm] = useDebounce(inputValue, 500);
  const [selectedSources, setSelectedSources] = useState<number[]>([]);
  const [currentMediaIndex, setCurrentMediaIndex] = useState<number>(-1);
  const [dragPosition, setDragPosition] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);
  const [availableEvidence, setAvailableEvidence] = useState<{
    url: string;
    mediaType: string;
    status: string;
  }[]>([]);
  const [loadingEvidence, setLoadingEvidence] = useState(false);
  const [evidenceSources, setEvidenceSources] = useState<SourceObject[]>([]);
  const [sourceFromSessionStorage, setSourceFromSessionStorage] = useState<SourceObject | null>(null);
  const [previewPopoverOpen, setPreviewPopoverOpen] = useState(-1);
  const [previewSourceInfo, setPreviewSourceInfo] = useState<SourceInfo | null>(null);
  const [loadingPreviewSourceInfo, setLoadingPreviewSourceInfo] = useState(true);
  const [copiedToMediaShareIndex, setCopiedToMediaShareIndex] = useState<number | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const addButtonRef = useRef<HTMLDivElement>(null);
  const evidenceQueryRef = useRef<string | undefined>();
  const previewPopoverRef = useRef<HTMLDivElement>(null);
  const valueEffectRunRef = useRef(0);
  const {
    isFullscreen
  } = useContext(FullScreenContext);
  const {
    idToken,
    accessToken
  } = useContext(AuthContext);
  const featuresContext = useContext(FeaturesContext);
  const featureFlags = featuresContext?.featureFlags || {};
  const mediaSourceShare = useMediaSourceShare();
  const registerApplyCaptureToEventMedia = useToolsDrawer()?.registerApplyCaptureToEventMedia;
  const registerAddSharedSourceToEvent = useToolsDrawer()?.registerAddSharedSourceToEvent;
  const updateMediaContextRef = useRef<((field: string | object, value?: any) => void) | null>(null);
  const handleAddSourcesRef = useRef<(sourcesToAdd?: SourceObject[]) => void>(() => {});
  const availableSource = sessionStorage.getItem(`source`);
  useEffect(() => {
    if (previewPopoverOpen !== -1 && sourcesForResults?.[previewPopoverOpen]?.sourceUrl) {
      setLoadingPreviewSourceInfo(true);
      setPreviewSourceInfo(null);
      try {
        getCurrentSourceInfo(sourcesForResults?.[previewPopoverOpen]?.sourceUrl).then(sourceInfoObject => {
          setPreviewSourceInfo(sourceInfoObject);
          setLoadingPreviewSourceInfo(false);
        });
      } catch (error) {
        setPreviewSourceInfo(null);
        setLoadingPreviewSourceInfo(false);
      }
    } else {
      setPreviewSourceInfo({} as SourceInfo);
      setLoadingPreviewSourceInfo(false);
    }
  }, [previewPopoverOpen, sourcesForResults]);
  useEffect(() => {
    if (availableSource) {
      const sourceFromSessionStorage = JSON.parse(availableSource);
      if (sourceFromSessionStorage.source) {
        sourceFromSessionStorage.source.caseId = sourceFromSessionStorage.caseId;
        setSourceFromSessionStorage(sourceFromSessionStorage.source);
      } else {
        setSourceFromSessionStorage(null);
      }
    } else {
      setSourceFromSessionStorage(null);
    }
  }, [availableSource]);
  const addSourceFromSessionStorage = () => {
    if (sourceFromSessionStorage) {
      const sourceType = extractPathAfterBulk(sourceFromSessionStorage.sourceFilePath || '').sourceType || 'Unknown';
      const sourceObject = {
        sourceType: sourceType,
        sourceUrl: sourceFromSessionStorage.sourceFilePath,
        truncatedFilePath: sourceFromSessionStorage.truncatedFilePath,
        sourceContext: sourceFromSessionStorage.sourceContext,
        pageNumber: sourceFromSessionStorage.pageNumber
      };
      handleAddSources([sourceObject]);
    }
  };
  useEffect(() => {
    const isSearchTermValid = searchTerm && searchTerm.length > 2;
    let filesSearchTermToUse = searchTerm;
    if (filesSearchTermToUse.startsWith('"') && filesSearchTermToUse.endsWith('"')) {
      filesSearchTermToUse = filesSearchTermToUse.slice(1, -1);
    } else {
      filesSearchTermToUse = filesSearchTermToUse.trim();
    }
    if (!caseId || caseId.trim() === '' || evidenceQueryRef.current === `${caseId}-${isSearchTermValid ? filesSearchTermToUse : ''}`) return;
    evidenceQueryRef.current = `${caseId}-${isSearchTermValid ? filesSearchTermToUse : ''}`;
    const fetchEvidence = async () => {
      setLoadingEvidence(true);
      const evidenceUrl = !!featureFlags?.provenanceService ? '/api/protected/provenance-service-proxy' : '/api/protected/evidence';
      const body = {
        caseId: caseId,
        search: filesSearchTermToUse
      };
      const filesResponse = await FetchWithVersionCheck(evidenceUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'x-tai-id-token': idToken || ''
        },
        body: JSON.stringify(body)
      });
      const filesResponseJson = await filesResponse.json();
      let fileSources: EvidenceObject[] = [];
      if (!!featureFlags?.provenanceService && Object.hasOwn(filesResponseJson, "statuses")) {
        if (filesResponseJson["statuses"].length === 0) {
          setAvailableEvidence([]);
          setLoadingEvidence(false);
          return;
        }
        fileSources = filesResponseJson["statuses"].map((source: any) => {
          // Try multiple possible field names for status
          const statusValue = source.provenance_status || source.verification_status || source.status || source.state || source.condition || 'unknown';
          return {
            ...source,
            url: source.source_s3_path,
            mediaType: source.file_type,
            status: statusValue
          };
        });
      } else {
        // When not using provenance service, ensure sources have status field
        fileSources = filesResponseJson.sources?.map((source: any) => ({
          ...source,
          status: source.status || 'unknown'
        })) || [];
      }
      setAvailableEvidence(fileSources as any);
      setLoadingEvidence(false);
    };
    try {
      fetchEvidence();
    } catch (error) {
      setAvailableEvidence([]);
      setLoadingEvidence(false);
    }
  }, [caseId, searchTerm]);
  useEffect(() => {
    let newEvidenceSources: SourceObject[] = [];
    availableEvidence?.forEach(evidence => {
      const evidenceSourceObject: SourceObject = {
        sourceType: evidence.mediaType,
        sourceUrl: evidence.url,
        status: evidence.status || 'unknown'
      };
      newEvidenceSources.push(evidenceSourceObject);
    });
    setEvidenceSources(newEvidenceSources);
  }, [availableEvidence]);
  useEffect(() => {
    const uniqueSources = deduplicateSources(value);
    setNewSources(uniqueSources);
    const runId = ++valueEffectRunRef.current;
    let cancelled = false;
    const enrichSources = async () => {
      const sourcesWithUrl = uniqueSources.filter(s => !!s.sourceUrl);
      // Get unique sourceUrls so that we only fetch source info for each unique sourceUrl and not for each source object
      const uniqueSourceUrls = [...new Set(sourcesWithUrl.map(s => s.sourceUrl!))];
      if (uniqueSourceUrls.length === 0) return;
      const enrichedByKey = new Map<string, {
        sourceInfo: SourceInfo | null;
        sourceType: string | undefined;
      }>();
      // Fetch source info for each unique sourceUrl
      await Promise.all(uniqueSourceUrls.map(async sourceUrl => {
        const sourceInfo = await getCurrentSourceInfo(sourceUrl);
        if (cancelled || runId !== valueEffectRunRef.current) return;
        const representative = uniqueSources.find(s => s.sourceUrl === sourceUrl);
        const mediaType = getMediaType(representative ?? {
          mediaType: 'unknown',
          mediaUrl: sourceUrl
        } as MediaObject, sourceInfo);
        enrichedByKey.set(sourceUrl, {
          sourceInfo: sourceInfo ?? null,
          sourceType: sourceInfo ? mediaType : uniqueSources.find(s => s.sourceUrl === sourceUrl)?.sourceType
        });
      }));
      if (cancelled || runId !== valueEffectRunRef.current) return;
      const merged = uniqueSources.map(s => {
        if (!s.sourceUrl) return s;
        const enrichment = enrichedByKey.get(s.sourceUrl!);
        return enrichment ? {
          ...s,
          sourceInfo: enrichment.sourceInfo,
          sourceType: enrichment.sourceType
        } : s;
      });
      setNewSources(merged.filter((s): s is SourceObject => s !== null));
    };
    enrichSources();
    return () => {
      cancelled = true;
    };
  }, [value]);
  useEffect(() => {
    if (mediaValue && mediaValue.length > 0 && currentMediaIndex === -1) {
      // set the initial media index to the first media item
      setCurrentMediaIndex(0);
    }
    const mediaAsSources: SourceObject[] = [];
    if (isTitleSlide) {
      mediaValue.forEach(media => {
        const sourceUrl = media.mediaUrl || media.mediaSrc || (media as unknown as SourceObject).sourceUrl;
        const sourceType = media.mediaType || getMediaType(media, media.sourceInfo);
        mediaAsSources.push({
          sourceUrl,
          sourceType,
          sourceContext: media.mediaContext
        });
      });
    }
    const sourcesToCompare = deduplicateSources([...mediaAsSources, ...newSources]);
    if (!deepEqual(sourcesToCompare, newSources)) {
      const validSources = getValidSources(sourcesToCompare);
      const deduplicatedSources = deduplicateSources(validSources);
      if (isTitleSlide) {
        const onlySource = deduplicatedSources[0] || mediaAsSources[0];
        const mediaType = getMediaType(onlySource, onlySource.sourceInfo);
        const newMediaObject: MediaObject | null = onlySource && onlySource.sourceUrl ? {
          mediaType,
          mediaSrc: onlySource.sourceUrl,
          mediaUrl: onlySource.sourceUrl,
          mediaContext: {
            ...onlySource.sourceContext,
            pageNumber: onlySource.sourceContext?.pageNumber,
            page: onlySource.sourceContext?.pageNumber,
            startTime: onlySource.sourceContext?.startTime
          }
        } : null;
        if (onlySource && newMediaObject) {
          handleUnifiedSourcesChanges?.([onlySource], [newMediaObject]);
        } else {
          handleUnifiedSourcesChanges?.([], []);
        }
      } else {
        handleUnifiedSourcesChanges?.(deduplicatedSources, mediaValue);
      }
    }
  }, [newSources, mediaValue, isTitleSlide]);
  useEffect(() => {
    if (sourcesFromChat && sourcesFromChat.length > 0 || evidenceSources && evidenceSources.length > 0 || mediaSourcesFromChat && mediaSourcesFromChat.length > 0) {
      setSourcesForResults([]);
      const sourcesFromMedia = mediaSourcesFromChat ? mediaSourcesFromChat.map(mediaSource => {
        const resolvedType = getMediaType(mediaSource, mediaSource.sourceInfo);
        return {
          sourceUrl: mediaSource.mediaUrl,
          mediaUrl: mediaSource.mediaUrl,
          mediaSrc: mediaSource.mediaUrl,
          mediaType: resolvedType,
          sourceType: resolvedType,
          sourceContext: mediaSource.mediaContext
        } as SourceObject;
      }) : [];
      const allSources = [...(sourcesFromChat || []), ...(evidenceSources || []), ...(sourcesFromMedia || [])] as SourceObject[];
      const filteredSources = allSources.filter((source, index) => {
        // Skip if source is already selected
        if (newSources?.some(selectedSource => selectedSource.sourceUrl === source.sourceUrl && deepEqual(selectedSource.sourceContext, source.sourceContext))) {
          return false;
        }

        // If no search term, show all sources
        if ((!searchTerm || searchTerm.length <= 2) && index < SOURCES_LIST_LIMIT) {
          return true;
        }
        if (!searchTerm) return false;
        let searchTermToUse = searchTerm;
        if (searchTermToUse.startsWith('"') && searchTermToUse.endsWith('"')) {
          searchTermToUse = searchTermToUse.slice(1, -1);
        } else {
          searchTermToUse = searchTermToUse.trim();
        }
        const sourceUrl = source.sourceUrl?.toLowerCase() || '';
        let sourceType = '';
        let sourceType2 = '';
        let sourceType3 = '';
        if (Array.isArray(source.sourceType) && source.sourceType[0]) {
          sourceType = source.sourceType[0].toLowerCase() || '';
          if (source.sourceType[1]) {
            sourceType = source.sourceType[1].toLowerCase() || '';
          }
          if (source.sourceType[2]) {
            sourceType = source.sourceType[2].toLowerCase() || '';
          }
        } else if (source.sourceType && typeof source.sourceType === "string") {
          sourceType = source.sourceType.toLowerCase() || '';
        }
        const pageNumber = source.sourceContext?.pageNumber?.toString() || '';
        let searchStr = searchTermToUse.toLowerCase();
        // remove psuedoRegEx modifiers
        if (searchStr.startsWith('^')) {
          searchStr = searchStr.slice(1);
        } else if (searchStr.endsWith('$')) {
          searchStr = searchStr.slice(0, -1);
        }
        return sourceUrl.includes(searchStr) || sourceType.includes(searchStr) || sourceType2.includes(searchStr) || sourceType3.includes(searchStr) || pageNumber.includes(searchStr);
      });
      // Deduplicate the filtered results
      setSourcesForResults(deduplicateSources(filteredSources));
    } else {
      setSourcesForResults([]);
    }
  }, [sourcesFromChat, evidenceSources, searchTerm, mediaSourcesFromChat, newSources]);

  /*
  const handleMediaChanges = (mediaOnly: MediaObject[], currentMediaIndex?: number) => {
    if (handleUnifiedSourcesChanges) {
      const newSourcesCopy: SourceObject[] = [];
      let index = 0;
      newSources.forEach(source => {
        const updatedSource = { ...source };
        if (isSourceInMedia(source, mediaValue)) {
          if (index === currentMediaIndex) {
            updatedSource.sourceContext = { ...mediaOnly[index].mediaContext };
            updatedSource.pageNumber = mediaOnly[index].mediaContext?.pageNumber;
          }
          index++;
        }
        newSourcesCopy.push(updatedSource);
      });
      handleUnifiedSourcesChanges(newSourcesCopy, mediaOnly);
    }
  }
  */

  const handleReorderSources = (event: any, info: any, index: number) => {
    if (!editingEnabled) return;
    const draggedIndex = index;
    const targetIndex = dragPosition !== null ? dragPosition : Math.round(index + info.offset.y / 40);
    if (draggedIndex === targetIndex) {
      setDragPosition(null);
      setIsDragging(false);
      return;
    }
    const newOrder = [...newSources];
    const [draggedItem] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedItem);
    const newOrderMedia: MediaObject[] = [];
    newOrder.forEach(source => {
      if (isSourceInMedia(source, mediaValue)) {
        const newMediaObject = {
          mediaType: getMediaType(source, source.sourceInfo),
          mediaSrc: source.sourceUrl,
          mediaUrl: source.sourceUrl,
          mediaContext: source.sourceContext
        };
        newOrderMedia.push(newMediaObject);
      }
    });
    if (handleUnifiedSourcesChanges) {
      handleUnifiedSourcesChanges(newOrder, newOrderMedia);
    }
    setDragPosition(null);
    setIsDragging(false);

    // Keep the blue color for 5 seconds after dropping
    const targetItemIndex = newOrder.findIndex(item => item.sourceUrl === draggedItem.sourceUrl && item.sourceContext?.pageNumber === draggedItem.sourceContext?.pageNumber);
    if (targetItemIndex !== -1) {
      setDraggedItemIndex(targetItemIndex);
      setTimeout(() => {
        setDraggedItemIndex(null);
      }, 500);
    }
  };
  const removeSource = (index: number) => {
    const sourceToRemove = newSources[index];
    const lessSources = newSources.filter((_, i) => i !== index);
    const deduplicatedSources = deduplicateSources(lessSources);

    // Remove associated media if it exists
    let deduplicatedMedia = mediaValue;
    if (sourceToRemove && isMediaSource(sourceToRemove, sourceToRemove.sourceInfo)) {
      const updatedMedia = mediaValue.filter(media => {
        const mediaPage = media.mediaContext?.pageNumber || media.mediaContext?.page;
        const sourcePage = sourceToRemove.sourceContext?.pageNumber || sourceToRemove.pageNumber;
        const mediaStartTime = media.mediaContext?.startTime;
        const sourceStartTime = sourceToRemove.sourceContext?.startTime;
        return !((media.mediaSrc === sourceToRemove.sourceUrl || media.mediaUrl === sourceToRemove.sourceUrl) && (!sourcePage && !mediaPage && !sourceStartTime && !mediaStartTime || sourcePage && mediaPage && sourcePage === mediaPage && !sourceStartTime && !mediaStartTime || sourceStartTime && mediaStartTime && sourceStartTime === mediaStartTime));
      });
      deduplicatedMedia = deduplicateSources(updatedMedia as any) as any;
    }
    handleUnifiedSourcesChanges?.(deduplicatedSources, deduplicatedMedia);
  };
  const toggleMediaSource = (source: SourceObject) => {
    if (!isMediaSource(source, source.sourceInfo)) return;
    const mediaType = getMediaType(source, source.sourceInfo) as MediaType;
    const sourcePage = source.sourceContext?.pageNumber || source.pageNumber;
    const sourceStartTime = source.sourceContext?.startTime;
    if (isSourceInMedia(source, mediaValue)) {
      // Remove from media - only remove the exact match with the same start time
      const updatedMedia = mediaValue.filter(media => {
        const mediaPage = media.mediaContext?.pageNumber || media.mediaContext?.page;
        const mediaStartTime = media.mediaContext?.startTime;
        const urlMatch = media.mediaSrc === source.sourceUrl || media.mediaUrl === source.sourceUrl;
        const pageMatch = !sourcePage && !mediaPage || sourcePage === mediaPage || sourcePage === 1 && !mediaPage;
        const timeMatch = !sourceStartTime && !mediaStartTime || sourceStartTime === mediaStartTime;
        const returnValue = !urlMatch || urlMatch && (!pageMatch || !timeMatch);
        return returnValue;
      });
      handleUnifiedSourcesChanges?.(newSources, updatedMedia);
    } else {
      // Add to media - include the start time in the media context
      const newMediaObject: MediaObject = {
        mediaType,
        mediaSrc: source.sourceUrl,
        mediaUrl: source.sourceUrl,
        mediaContext: {
          ...source.sourceContext,
          pageNumber: sourcePage,
          page: sourcePage,
          startTime: sourceStartTime
        }
      };
      handleUnifiedSourcesChanges?.(newSources, [...mediaValue, newMediaObject]);
    }
  };
  const getSourceForMedia = (media: MediaObject) => {
    let sourceIndex = -1;
    for (let index = 0; index < newSources.length; index++) {
      const source = newSources[index];
      if (source.sourceUrl === media.mediaUrl || source.sourceUrl === media.mediaSrc) {
        const sourcePage = source.sourceContext?.pageNumber || source.pageNumber || 1;
        const mediaPage = media.mediaContext?.pageNumber || media.mediaContext?.page || 1;
        if (sourcePage === mediaPage) {
          sourceIndex = index;
          break;
        }
      }
    }
    return sourceIndex;
  };
  const updateMediaContext = (field: string | object, value?: any) => {
    if (!editingEnabled) return;
    const sourceIndex = getSourceForMedia(mediaValue[currentMediaIndex]);
    let updatedSources: SourceObject[] = [...newSources];
    if (sourceIndex !== -1) {
      const sourceForMedia = {
        ...newSources[sourceIndex]
      };
      if (typeof field === 'string') {
        sourceForMedia.sourceContext = {
          ...sourceForMedia.sourceContext,
          [field as string]: value,
          lastUpdated: Date.now()
        };
      } else {
        sourceForMedia.sourceContext = {
          ...sourceForMedia.sourceContext,
          ...field,
          lastUpdated: Date.now()
        };
      }
      updatedSources = newSources.slice(0, sourceIndex);
      updatedSources.push(sourceForMedia);
      updatedSources.push(...newSources.slice(sourceIndex + 1));
    }
    const updatedMedia = mediaValue.map((media, index) => {
      if (index === currentMediaIndex) {
        if (typeof field === 'string') {
          return {
            ...media,
            mediaContext: {
              ...media.mediaContext,
              [field]: value,
              lastUpdated: Date.now()
            }
          };
        } else {
          return {
            ...media,
            mediaContext: {
              ...media.mediaContext,
              ...field,
              lastUpdated: Date.now()
            }
          };
        }
      }
      return media;
    });
    handleUnifiedSourcesChanges?.(updatedSources, updatedMedia);
  };
  updateMediaContextRef.current = updateMediaContext;
  useEffect(() => {
    if (!registerApplyCaptureToEventMedia) return;
    if (!editingEnabled || !isInModal || !mediaValue?.length) {
      registerApplyCaptureToEventMedia(null);
      return;
    }
    registerApplyCaptureToEventMedia((dataUrl: string) => {
      updateMediaContextRef.current?.("capture", dataUrl);
    });
    return () => registerApplyCaptureToEventMedia(null);
  }, [registerApplyCaptureToEventMedia, editingEnabled, isInModal, mediaValue?.length]);
  const handleAddSources = (sourcesToAdd?: SourceObject[]) => {
    const selectedSourcesToAdd = sourcesToAdd || selectedSources.map(index => sourcesForResults[index]);
    let updatedSources = [...newSources, ...selectedSourcesToAdd];
    if (isTitleSlide) {
      if (newSources.length > 0) return;
      updatedSources = updatedSources.slice(0, 1);
    }
    if (isTitleSlide) {
      const onlySource = updatedSources[0];
      // Add to media - include the start time in the media context
      const newMediaObject: MediaObject = {
        mediaType: getMediaType(onlySource, onlySource.sourceInfo),
        mediaSrc: onlySource.sourceUrl,
        mediaUrl: onlySource.sourceUrl,
        mediaContext: {
          ...onlySource.sourceContext,
          pageNumber: onlySource.sourceContext?.pageNumber,
          page: onlySource.sourceContext?.pageNumber,
          startTime: onlySource.sourceContext?.startTime
        }
      };
      updatedSources = deduplicateSources(updatedSources);
      handleUnifiedSourcesChanges?.(updatedSources, [newMediaObject]);
    } else {
      updatedSources = deduplicateSources(updatedSources);
      handleUnifiedSourcesChanges?.(updatedSources, mediaValue);
    }
    // Clear selected sources after adding
    setSelectedSources([]);
  };
  handleAddSourcesRef.current = handleAddSources;
  useEffect(() => {
    if (!registerAddSharedSourceToEvent) return;
    if (!editingEnabled || !isInModal) {
      registerAddSharedSourceToEvent(null);
      return;
    }
    registerAddSharedSourceToEvent((source: SourceObject) => {
      handleAddSourcesRef.current([source]);
    });
    return () => registerAddSharedSourceToEvent(null);
  }, [registerAddSharedSourceToEvent, editingEnabled, isInModal]);
  const handleRemoveAllSources = () => {
    handleUnifiedSourcesChanges?.([], []);
  };
  const calculateDragPosition = (event: any, draggedIndex: number) => {
    if (!listRef.current || !editingEnabled) return;
    const listRect = listRef.current.getBoundingClientRect();
    const listItems = listRef.current.getElementsByTagName('li');
    // Calculate the position relative to the list
    const clientY = event.clientY || event.touches?.[0]?.clientY;
    if (!clientY) return;
    const relativeY = clientY - listRect.top;
    // If we're dragging outside the list bounds, maintain the previous position
    if (relativeY < 0 || relativeY > listRect.height) {
      return;
    }
    // Find the position between items
    let position = 0;
    for (let i = 0; i < listItems.length; i++) {
      const itemRect = listItems[i].getBoundingClientRect();
      const itemTop = itemRect.top - listRect.top;
      const itemBottom = itemRect.bottom - listRect.top;
      const itemMiddle = itemTop + itemRect.height / 2;
      // Add a dead zone around the middle of each item
      const deadZoneSize = 10; // pixels
      const deadZoneTop = itemMiddle - deadZoneSize;
      const deadZoneBottom = itemMiddle + deadZoneSize;
      // If we're in the dead zone of the current item, maintain the previous position
      if (relativeY >= deadZoneTop && relativeY <= deadZoneBottom) {
        return;
      }
      // If we're above the middle of the current item, place before it
      if (relativeY < itemMiddle) {
        position = i;
        break;
      }
    }
    // If we're past the last item, set position to the end
    if (position === 0 && relativeY > listRect.height) {
      position = listItems.length;
    }
    // Adjust position to not show above or directly below the dragged item
    if (position === draggedIndex || position === draggedIndex + 1) {
      return;
    }
    setDragPosition(position);
  };
  useEffect(() => {
    if (previewPopoverOpen !== -1) {
      const handleClickOutside = (event: MouseEvent) => {
        if (previewPopoverRef.current && !previewPopoverRef.current.contains(event.target as Node)) {
          setPreviewPopoverOpen(-1);
        }
      };
      if (previewPopoverOpen === -1) {
        document.addEventListener('mousedown', handleClickOutside);
      } else {
        document.removeEventListener('mousedown', handleClickOutside);
      }
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [previewPopoverOpen]);
  return <div className="box-border flex flex-col sm:flex-row gap-8 w-full" data-sentry-component="UnifiedSources" data-sentry-source-file="unifiedSources.tsx">
      {/* Sources List - Left Side */}
      <div className="box-border flex flex-col justify-left max-w-full sm:max-w-1/2">
        <div className="box-border flex items-center justify-between gap-2 m-0 p-0 mb-[6px] mr-[10px]">
          <div className="box-border flex items-center gap-2">
            <label className="text-white/100 font-light tracking-wider text-[14px] m-0 p-0">
              Selected Sources ({newSources?.length})
            </label>
            <Tooltip content="These are the selected sources for this event. You can drag to reorder, add to media viewer, or remove sources." placement="top" delay={0} classNames={{
            content: 'tooltip bg-black/90 text-white/100 px-3 py-2 rounded-md text-sm z-[9999] w-[200px]'
          }} data-sentry-element="Tooltip" data-sentry-source-file="unifiedSources.tsx">
              <InfoIcon size={16} className="text-white/65 cursor-help" data-sentry-element="InfoIcon" data-sentry-source-file="unifiedSources.tsx" />
            </Tooltip>
          </div>
          {newSources?.length > 0 && <button className="text-brand-blue-light/80 text-sm hover:text-white font-semibold cursor-pointer" onClick={handleRemoveAllSources}>
              Remove All
            </button>}
        </div>
        <motion.ul ref={listRef} className='box-border flex flex-col space-y-2 w-full p-2 m-0 sm:max-h-[225px] overflow-auto custom-scrollbar2 mt-1 list-decimal list-outside border-1 border-white/10 border-solid rounded-md bg-white/5' data-sentry-element="unknown" data-sentry-source-file="unifiedSources.tsx">
          {newSources?.length === 0 && <div className="text-white text-sm text-center py-4 border-1 border-white/10 border-dashed rounded-md">
              No Sources currently selected.
            </div>}
          {newSources && newSources?.length > 0 && newSources.map((sourceObject: SourceObject, index: number) => {
          const uniqueKey = sourceObject.sourceUrl ? `${sourceObject.sourceUrl}-${index}` : `source-${index}`;
          return <React.Fragment key={uniqueKey}>
                {dragPosition === index && <motion.li className="h-[2px] bg-brand-blue my-2" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} />}
                <motion.li drag={editingEnabled} dragConstraints={{
              top: 0,
              bottom: 0,
              left: 0,
              right: 0
            }} dragDirectionLock={true} dragElastic={0} dragMomentum={false} dragTransition={{
              bounceStiffness: 100,
              bounceDamping: 10
            }} initial={false} whileDrag={{
              scale: 1.02,
              backgroundColor: "rgba(255,255,255,0.1)",
              boxShadow: "0px 5px 10px rgba(0,0,0,0.2)",
              cursor: "grabbing",
              zIndex: 50
            }} animate={{
              backgroundColor: "transparent",
              scale: 1,
              boxShadow: "none",
              zIndex: 0,
              x: 0
            }} transition={{
              duration: 0.2
            }} onDragStart={() => {
              setIsDragging(true);
              setDragPosition(null);
              setDraggedItemIndex(index);
            }} onDrag={event => {
              calculateDragPosition(event, index);
            }} onDragEnd={(event, info) => handleReorderSources(event, info, index)} className='box-border flex flex-row justify-left items-center gap-1 w-full p-1 m-0 rounded-md border-1 border-white/5 border-solid hover:bg-white/5' style={{
              position: 'relative',
              transformOrigin: 'center'
            }}>
                  <div className='flex flex-row items-center gap-1 w-full'>
                    {editingEnabled && <motion.button className='btn-h box-border flex justify-center items-center text-white/65 p-0 sm:p-1 m-0 cursor-grab active:cursor-grabbing' onPointerDown={e => e.stopPropagation()} animate={{
                  color: draggedItemIndex === index ? "#0F1D32" : "rgba(255,255,255,0.65)",
                  scale: draggedItemIndex === index ? 1.1 : 1,
                  opacity: draggedItemIndex === index ? 1 : 0.6,
                  backgroundColor: draggedItemIndex === index ? "#2B88F5" : "transparent"
                }} transition={{
                  duration: 0.2,
                  ease: "easeInOut"
                }} style={{
                  filter: draggedItemIndex === index ? 'drop-shadow(0 0 6px #2B88F5)' : 'none',
                  borderRadius: '4px'
                }}>
                        <motion.div animate={{
                    scale: draggedItemIndex === index ? [1, 1.2, 1] : 1
                  }} transition={{
                    duration: 0.4,
                    ease: "easeInOut",
                    times: [0, 0.25, 0.5, 1]
                  }}>
                          <DotsSixIcon size={20} weight="bold" />
                        </motion.div>
                      </motion.button>}
                    <div className='flex flex-row justify-between items-center gap-1 p-0 m-0 w-full'>
                      <Popover placement='bottom-start' showArrow={false} shouldFlip={true} triggerScaleOnOpen={false}>
                        <PopoverTrigger>
                          <button type="button" aria-label={`View source details: ${(() => {
                        const url = (sourceObject.sourceUrl || '').replace(/@/g, '');
                        const parts = url.split('/');
                        const filename = parts.pop() || '';
                        const parentDir = parts.pop() || '';
                        return `${parentDir}/${filename}`;
                      })()}`} aria-haspopup="dialog" className='flex-grow text-white/65 px-1 cursor-pointer w-[160px] text-right bg-transparent border-none p-0'>
                            <div data-source-type={`${getMediaType(sourceObject, sourceObject.sourceInfo)}/${sourceObject.sourceType || 'unknown'}`} className="flex-1 text-white/100 text-sm truncate dir-rtl">
                              &lrm;{(() => {
                            const url = (sourceObject.sourceUrl || '').replace(/@/g, '');
                            const parts = url.split('/');
                            const filename = parts.pop() || '';
                            const parentDir = parts.pop() || '';
                            const ann = formatUnifiedSourceListAnnotation(sourceObject);
                            return `${parentDir}/${filename}${ann ? ` ${ann}` : ''}`;
                          })()}
                            </div>
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className='min-w-[300px] max-w-[500px] bg-[#2C3A4A] rounded-md p-2 pt-0 border-1 border-gray-800 border-solid'>
                          <div className='text-white font-semibold bg-white/10 mb-2 border-b-1 border-white/20 border-solid p-2 rounded-t-md w-full'>
                            {path.basename(sourceObject.truncatedFilePath || sourceObject.sourceUrl || 'Unknown')}
                          </div>
                          <div className='flex flex-col gap-2 w-full p-0 m-0'>
                            {(() => {
                          const mt = getMediaType(sourceObject, sourceObject.sourceInfo);
                          if (mt === 'audio' || mt === 'video') {
                            return <>
                                    {sourceObject.sourceContext?.startTime != null && sourceObject.sourceContext.startTime !== '' && <div className='flex flex-row gap-1 w-full p-0 m-0'>
                                          <div className='flex flex-row p-0 m-0 font-bold text-nowrap pr-2'>Start:</div>
                                          <div className='flex flex-row p-0 m-0'>
                                            <div className='box-border max-h-[30px] bg-transparent text-white p-0 m-0 truncate resize-none outline-none font-inter'>
                                              {sourceObject.sourceContext.startTime}
                                            </div>
                                          </div>
                                        </div>}
                                    {sourceObject.sourceContext?.endTime != null && sourceObject.sourceContext.endTime !== '' && <div className='flex flex-row gap-1 w-full p-0 m-0'>
                                          <div className='flex flex-row p-0 m-0 font-bold text-nowrap pr-2'>End:</div>
                                          <div className='flex flex-row p-0 m-0'>
                                            <div className='box-border max-h-[30px] bg-transparent text-white p-0 m-0 truncate resize-none outline-none font-inter'>
                                              {sourceObject.sourceContext.endTime}
                                            </div>
                                          </div>
                                        </div>}
                                  </>;
                          }
                          const pageRef = resolveUnifiedSourcePageRef(sourceObject);
                          return pageRef ? <div className='flex flex-row gap-1 w-full p-0 m-0'>
                                  <div className='flex flex-row p-0 m-0 font-bold text-nowrap pr-2'>Page:</div>
                                  <div className='flex flex-row p-0 m-0'>
                                    <div className='box-border max-h-[30px] bg-transparent text-white p-0 m-0 truncate resize-none outline-none font-inter'>
                                      {pageRef}
                                    </div>
                                  </div>
                                </div> : null;
                        })()}
                            <div className='flex flex-row gap-1 w-full p-0 m-0'>
                              <div className='p-0 m-0 font-bold text-nowrap pr-1'>File Location:</div>
                              <div className='box-border max-h-[60px] bg-transparent text-white p-0 m-0 break-all outline-none font-inter overflow-y-auto custom-scrollbar2 flex-1'>
                                {extractPathAfterBulk(sourceObject.truncatedFilePath || sourceObject.sourceUrl || 'Unknown', true).truncatedFilePath}
                              </div>
                              <Tooltip content="Copy file location to clipboard" placement="top" delay={0} classNames={{
                            content: 'tooltip bg-black/90 text-white/100 px-2 py-1 rounded-md text-sm z-[9999] w-[200px] whitespace-nowrap'
                          }}>
                                <button className='btn-h box-border flex justify-center items-center text-white/65 p-1 m-0 hover:text-white/100 h-[20px]' onClick={e => {
                              e.stopPropagation();
                              if (sourceObject.sourceUrl) {
                                navigator.clipboard.writeText(sourceObject.truncatedFilePath || sourceObject.sourceUrl);
                              }
                            }}>
                                  <FileCloudIcon size={16} />
                                </button>
                              </Tooltip>
                              {isMediaSource(sourceObject, sourceObject.sourceInfo) && mediaSourceShare?.addToMediaShare && <Tooltip content={copiedToMediaShareIndex === index ? "Added" : "Add to Favorites"} placement="top" delay={0} classNames={{
                            content: 'tooltip bg-black/90 text-white/100 px-2 py-1 rounded-md text-sm z-[9999] w-[200px] whitespace-nowrap'
                          }}>
                                  <button className={`btn-h box-border flex justify-center items-center p-1 m-0 h-[20px] ${copiedToMediaShareIndex === index ? 'text-green-500' : 'text-white/65 hover:text-white/100'}`} onClick={e => {
                              e.stopPropagation();
                              mediaSourceShare.addToMediaShare(sourceObject, () => {
                                setCopiedToMediaShareIndex(index);
                                setTimeout(() => setCopiedToMediaShareIndex(null), 1500);
                              });
                            }} aria-label={copiedToMediaShareIndex === index ? "Added" : "Add to Favorites"}>
                                    {copiedToMediaShareIndex === index ? <CheckIcon size={16} /> : <BookmarksSimpleIcon size={16} />}
                                  </button>
                                </Tooltip>}
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                      
                      <div className='flex flex-row gap-2'>
                        <button className={`
                            btn-h box-border flex justify-center items-center p-0 sm:p-1 m-0
                            ${isRestrictedFileType(sourceObject) ? 'text-white/20 cursor-not-allowed' : 'text-white/65 cursor-pointer'}
                          `} onClick={e => {
                      e.stopPropagation();
                      handleDownload(sourceObject, !!featureFlags?.useIntercom);
                    }} disabled={isRestrictedFileType(sourceObject)}>
                          <DownloadSimpleIcon size={20} />
                        </button>
                        
                        {isMediaSource(sourceObject, sourceObject.sourceInfo) ? <button className={`btn-h box-border flex justify-center items-center p-0 sm:p-1 m-0 
                              ${isSourceInMedia(sourceObject, mediaValue) ? 'text-green-500' : 'text-white/65'}`} onClick={e => {
                      e.stopPropagation();
                      toggleMediaSource(sourceObject);
                    }} title={isSourceInMedia(sourceObject, mediaValue) ? 'Remove from media viewer' : 'Add to media viewer'}>
                            {isSourceInMedia(sourceObject, mediaValue) ? <MinusSquareIcon size={20} /> : <PlusSquareIcon size={20} />}
                          </button> : <div className="w-[28px]" />}

                        {editingEnabled && <button className='btn-h box-border flex justify-center items-center text-white/65 p-0 sm:p-1 m-0' onClick={e => {
                      e.stopPropagation();
                      removeSource(newSources.indexOf(sourceObject));
                    }}>
                            <XIcon size={20} />
                          </button>}
                      </div>
                    </div>
                  </div>
                </motion.li>
              </React.Fragment>;
        })}
          {dragPosition === newSources?.length && <motion.li className="h-[2px] bg-brand-blue my-2" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} />}
        </motion.ul>
        {editingEnabled && !(isTitleSlide && newSources.length > 0) && <div className="box-border flex flex-col m-0 mt-[15px] justify-start w-full sm:w-[350px] border-1 border-white/10 border-solid rounded-md p-4 bg-white/5">
            <div className="flex flex-row items-center justify-between mb-[10px]">
              <div className="flex flex-row items-center gap-2">
                <div className="text-white/100 font-light tracking-wider text-[14px] m-0 p-0">Add Sources</div>
                <Tooltip content="Search and add sources from the available list. Type at least 3 characters to search." placement="top" delay={0} classNames={{
              content: 'tooltip bg-black/90 text-white/100 px-3 py-2 rounded-md text-sm z-[9999] w-[200px]'
            }}>
                  <InfoIcon size={16} className="text-white/65 cursor-help" />
                </Tooltip>
              </div>
              {sourceFromSessionStorage && <div className="flex flex-row items-center">
                  <Tooltip content={<div>
                        Add source copied from Assistant&nbsp;
                        {(sourceFromSessionStorage as any)?.caseId?.trim() !== caseId && <span>
                            in the <strong>{(sourceFromSessionStorage as any)?.caseId?.trim()}</strong> case.
                          </span>}
                      </div>} placement="top" delay={0} classNames={{
              content: 'tooltip bg-black/90 px-3 py-2 rounded-md text-sm z-[9999] w-[200px]'
            }}>
                    <button className="btn-h box-border flex justify-center items-center text-white/65 p-0 sm:p-1 m-0" onClick={addSourceFromSessionStorage}>
                      <ListPlusIcon size={20} />
                    </button>
                  </Tooltip>
                </div>}
            </div>
            <div>
              <div className="flex flex-row justify-between items-center p-2 bg-white text-black/90 rounded-md cursor-pointer" onClick={e => {
            const input = e.currentTarget.querySelector('input');
            input?.focus();
          }}>
                <input type="text" className="box-border text-[14px] w-full outline-none" placeholder="Type at least 3 characters to search" value={inputValue || ''} onChange={e => setInputValue(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }} />
                <ListMagnifyingGlassIcon size={20} />
              </div>
              {loadingEvidence && <div className="flex items-center justify-center h-full pb-8">
                  <div className="animate-pulse font-bold">Loading evidence...</div>
                </div>}
              {!loadingEvidence && sourcesForResults?.length > 0 ? <div className="w-full border border-white/20 rounded-sm mt-3">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white/65 text-sm">
                        {sourcesForResults.length} result(s)
                      </span>
                      {sourcesForResults.length > 0 && <button className="text-brand-blue-light/80 text-sm hover:text-white pr-2 cursor-pointer font-semibold" onClick={() => {
                  if (selectedSources.length === sourcesForResults.length) {
                    setSelectedSources([]);
                  } else {
                    setSelectedSources(sourcesForResults.map((_, index) => index));
                  }
                }}>
                          {selectedSources.length === sourcesForResults.length ? 'Deselect All' : 'Select All'}
                        </button>}
                    </div>
                    <div className="max-h-[260px] overflow-y-auto custom-scrollbar2 pr-2 flex flex-col gap-1">
                      {sourcesForResults.map((sourceObject, index) => {
                  const isAddSourcePreviewOpen = previewPopoverOpen === index;
                  const addSourceResolvedMediaType = isAddSourcePreviewOpen && !loadingPreviewSourceInfo ? getMediaType(sourceObject, previewSourceInfo ?? sourceObject.sourceInfo) : null;
                  const canShowAddSourceMediaPreview = addSourceResolvedMediaType !== null && UsableMediaTypes.includes(addSourceResolvedMediaType);
                  return <div key={`${sourceObject.sourceUrl}-${index}`} className="box-border px-2 py-1 flex flex-row justify-left items-center gap-1 rounded-md border-1 border-white/5 border-solid hover:bg-white/5 cursor-pointer">
                          <Popover placement='right' showArrow={true} shouldFlip={true} offset={40} crossOffset={2} autoFocus={true} className="outline-none" classNames={{
                      base: 'outline-none before:bg-gray-700 before:rounded-[2px] before:shadow-none before:drop-shadow-none border-0 before:w-[25px] before:h-[25px]'
                    }} isOpen={previewPopoverOpen === index} onKeyDown={e => {
                      if (e.key === 'Escape') {
                        e.stopPropagation();
                        setPreviewPopoverOpen(-1);
                      }
                    }}>
                            <PopoverTrigger>
                              <button type="button" aria-label={`Preview source: ${(() => {
                          const url = (sourceObject.sourceUrl || '').replace(/@/g, '');
                          const parts = url.split('/');
                          const filename = parts.pop() || '';
                          const parentDir = parts.pop() || '';
                          return `${parentDir}/${filename}`;
                        })()}`} aria-haspopup="dialog" className="flex-1 text-white/100 text-sm truncate dir-rtl bg-transparent border-none p-0 cursor-pointer" onClick={e => {
                          e.stopPropagation();
                          setPreviewPopoverOpen(index);
                        }}>
                                &lrm;{(() => {
                            const url = (sourceObject.sourceUrl || '').replace(/@/g, '');
                            const parts = url.split('/');
                            const filename = parts.pop() || '';
                            const parentDir = parts.pop() || '';
                            const ann = formatUnifiedSourceListAnnotation(sourceObject);
                            return `${parentDir}/${filename}${ann ? ` ${ann}` : ''}`;
                          })()}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="max-w-[450px] bg-[#2C3A4A] rounded-md border-1 border-gray-800 border-solid overflow-y-auto custom-scrollbar2">
                              <div ref={previewPopoverRef} tabIndex={0} className="flex flex-col justify-start items-center gap-2 w-[450px] h-[650px] outline-none" onKeyDown={e => {
                          if (e.key === 'Escape') {
                            e.stopPropagation();
                            setPreviewPopoverOpen(-1);
                          }
                        }}>
                                <div className="box-border flex flex-row justify-between items-start gap-1 pl-[16px] pr-1 w-full">
                                  <div className="text-white/100 text-sm break-all bg-gray-900 rounded-sm my-1 p-1">
                                    {extractPathAfterBulk(sourceObject.truncatedFilePath || sourceObject.sourceUrl || 'Unknown', true).truncatedFilePath}
                                  </div>
                                  <div className="cursor-pointer h-[24px]" onClick={e => {
                              e.stopPropagation();
                              setPreviewPopoverOpen(-1);
                            }}>
                                    <XIcon className="top-[0px] right-[5px]" size={20} />
                                  </div>
                                </div>
                                {loadingPreviewSourceInfo ? <div className="flex flex-col items-center justify-center gap-4 h-full pb-8">
                                    <Spinner size="lg" color='white' className="opacity-70" />
                                    <div className="animate-pulse font-bold">Loading source info...</div>
                                  </div> : !canShowAddSourceMediaPreview ? <div className="flex flex-col items-center justify-center gap-4 h-full pb-8 px-6 text-center text-white/80 text-sm">
                                    Preview is not available for this file type.
                                  </div> : <div className="previewContainer flex flex-row justify-center items-center w-[420px] min-w-[420px] h-[540px] min-h-[540px]">
                                    <MediaDisplay mediaItem={{
                              sourceInfo: previewSourceInfo,
                              mediaType: addSourceResolvedMediaType,
                              mediaUrl: sourceObject.sourceUrl,
                              mediaSrc: sourceObject.sourceUrl,
                              mediaContext: sourceObject.sourceContext
                            } as MediaObject} containerHeight={520} containerWidth={420} isPreview={false} caseId={caseId} />
                                  </div>}
                              </div>
                            </PopoverContent>
                          </Popover>
                          {false && !!featureFlags?.provenanceService && <Tooltip className="w-[300px] max-w-[95vw]" placement="right" delay={100} offset={4} showArrow={true} classNames={{
                      content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
                      base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none before:border-0'
                    }} content={`Status: ${(sourceObject as any).status || 'Unknown'} - ${getStatusDescription((sourceObject as any).status)}`}>
                              <div className={`flex flex-col items-center gap-1 p-1 rounded-md transition-colors duration-200 ${!!featureFlags?.provenanceService && (sourceObject as any).status ? 'bg-black/20 hover:bg-black/10' : ''}`} role="status" aria-label={`Status: ${(sourceObject as any).status || 'Unknown'} - ${getStatusDescription((sourceObject as any).status)}`} key={`status-${(sourceObject as any).url}-${(sourceObject as any).status}`}>
                                <ProvenanceIcon status={(sourceObject as any).status} size={isStatus((sourceObject as any).status, 'ERROR') || isStatus((sourceObject as any).status, 'SUCCESS') ? 22 : 20} className={`h-[20px] w-[20px] min-w-[20px] min-h-[20px] ${getStatusColor((sourceObject as any).status)} ${!!featureFlags?.provenanceService && (sourceObject as any).status ? 'drop-shadow-sm border border-current/20 rounded-sm' : ''} ${isStatus((sourceObject as any).status, 'SUCCESS') ? 'drop-shadow-lg' : ''} ${isStatus((sourceObject as any).status, 'SUCCESS') ? 'border-2 border-green-300 rounded-md' : isStatus((sourceObject as any).status, 'ERROR') ? 'border-2 border-red-300 rounded-md' : isStatus((sourceObject as any).status, 'INTERMEDIARY') ? 'border-2 border-yellow-300 rounded-md' : ''}`} aria-hidden="true" />
                              </div>
                            </Tooltip>}
                          <input type="checkbox" checked={selectedSources.includes(index)} onChange={e => {
                      e.stopPropagation();
                      setPreviewPopoverOpen(-1);
                      const newSelected = e.target.checked ? isTitleSlide ? [index] : [...selectedSources, index] : isTitleSlide ? [] : selectedSources.filter(i => i !== index);
                      setSelectedSources(newSelected);
                      // If a source was checked, scroll to the add button container
                      if (e.target.checked && addButtonRef.current) {
                        setTimeout(() => {
                          addButtonRef.current?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'nearest'
                          });
                        }, 100);
                      }
                    }} className="w-4 h-4 rounded border-white/20 text-brand-blue focus:ring-brand-blue cursor-pointer" />
                        </div>;
                })}
                    </div>
                    <div ref={addButtonRef} className="mt-4 flex justify-end">
                      {selectedSources?.length > 0 && <button className={`
                            px-4 py-2 text-white cursor-pointer rounded-md hover:bg-brand-blue/80 transition-colors duration-200
                            ${isTitleSlide && newSources.length > 0 ? 'bg-brand-blue/30 hover:bg-brand-blue/20 cursor-not-allowed' : 'bg-brand-blue hover:bg-brand-blue/80'}
                          `} onClick={() => handleAddSources()} disabled={isTitleSlide && newSources.length > 0}>
                          Add {selectedSources.length} Selected
                        </button>}
                    </div>
                  </div>
                </div> : <>
                  {!loadingEvidence && searchTerm && searchTerm.length > 2 ? <div className="text-white/65 text-sm text-center py-4">
                      No results match your search.
                    </div> : null}
                </>}
            </div>
          </div>}
        {editingEnabled && isTitleSlide && newSources.length > 0 && <div className="box-border flex flex-col m-0 mt-[15px] justify-start w-full sm:w-[350px] border-1 border-white/10 border-solid rounded-md p-4 bg-white/5">
            <div className="flex flex-col items-center justify-center gap-4 py-8">
              <div className="text-white/80 text-sm text-center">
                Only one source is supported for a title slide.
              </div>
              <button className="px-4 py-2 text-gray-800 bg-gray-300 hover:bg-gray-400 cursor-pointer rounded-md transition-colors duration-200" onClick={handleRemoveAllSources}>
                Remove Current Source
              </button>
            </div>
          </div>}
      </div>

      {/* Media Display - Right Side */}
      {newSources?.length > 0 && <div className="box-border flex flex-col max-w-1/2 gap-3">
          <div className="flex items-center gap-2 mb-[1px]">
            <label className="text-white/100 font-light tracking-wider text-[14px] m-0 p-0">Selected Media</label>
            <Tooltip content="These are the selected media for this event. Click to view, drag to reorder, or remove media." placement="top" delay={0} classNames={{
          content: 'tooltip bg-black/90 text-white/100 px-3 py-2 rounded-md text-sm z-[9999] w-[200px]'
        }}>
              <InfoIcon size={16} className="text-white/65 cursor-help" />
            </Tooltip>
          </div>
          {mediaValue?.length < 1 ? <div className="text-white/65 text-sm text-center py-4">
              Click the source plus button to add media.
            </div> : <div className="box-border flex flex-col justify-start items-start gap-3">
              <div className={`box-border flex flex-row justify-start items-start gap-2
                  p-0 m-0 overflow-x-auto custom-scrollbar2
                  ${mediaValue.length >= 3 ? 'w-[470px] min-w-[470px]' : 'w-[375px] min-w-[375px]'}
                `}>
                {mediaValue.map((media, index) => <div key={`${media.mediaUrl}-${index}`} className="box-border relative flex flex-col items-start justify-start p-1 rounded-sm cursor-pointer" onClick={() => setCurrentMediaIndex(index)}>
                    <div className="z-[1001] flex flex-row justify-end w-[calc(100%+3px)] h-[10px] max-h-[10px] overflow-visible">
                      <button className={`btn-h rounded-sm p-0 m-0 bg-black text-white/65 hover:text-white/90 h-[16px] w-[16px] min-h-[16px] min-w-[16px]
                          ${currentMediaIndex === index ? 'opacity-100' : 'opacity-0'}`} onClick={e => {
                e.stopPropagation();
                const updatedMedia = mediaValue.filter((_, i) => i !== index);
                handleUnifiedSourcesChanges?.(newSources, updatedMedia);
                if (currentMediaIndex >= updatedMedia.length) {
                  setCurrentMediaIndex(Math.max(0, updatedMedia.length - 1));
                }
              }}>
                        <XIcon size={16} weight="bold" />
                      </button>
                    </div>
                    <div className={`z-[1000] box-border flex flex-row items-center justify-center w-[122px] overflow-hidden
                        rounded m-0 p-0 h-[110px] min-h-[110px] bg-white/5 border-1 border-solid
                        ${currentMediaIndex === index ? 'opacity-100 border-white/50' : 'opacity-30 hover:border-white/10'}
                      `}>
                      {!media.mediaSrc && !media.mediaUrl && <div className='p-2 flex justify-center items-center h-full'>
                          <span className='text-center line-clamp-3 overflow-hidden text-ellipsis'>No Media</span>
                        </div>}
                      {!isFullscreen && media.mediaType && UsableMediaTypes.includes(media.mediaType) && (media.mediaSrc || media.mediaUrl) && <div className="flex flex-col items-center justify-center w-full h-full">
                          <MediaDisplay mediaItem={media} isUnifiedSources={true} containerHeight={100} containerWidth={120} caseId={caseId} />
                        </div>}
                      {media.mediaType === 'youtube' && (media.mediaSrc || media.mediaUrl) && <img src={`https://img.youtube.com/vi/${media.mediaSrc?.split('/').pop() || media.mediaUrl?.split('/').pop()}/0.jpg`} alt="YouTube thumbnail" className='w-full h-full object-cover rounded cursor-pointer' />}
                    </div>
                    <div className="text-white/65 text-[11px] mt-1 text-center w-full">
                      {(() => {
                const filename = (media.mediaUrl || media.mediaSrc || '').split('/').pop() || '';
                const isAv = media.mediaType === 'audio' || media.mediaType === 'video';
                const startTime = formatTimeIndex(media.mediaContext?.startTime || '0');
                const endTime = formatTimeIndex(media.mediaContext?.endTime || '0');
                const startTimeInfo = isAv && startTime && startTime !== '0' && startTime !== '0.0' ? ` (Start: ${startTime})` : '';
                const endTimeInfo = isAv && endTime && endTime !== '0' && endTime !== '0.0' ? ` (End: ${endTime})` : '';
                const timeInfo = `${startTimeInfo}${endTimeInfo}`;
                const truncatedFilename = filename.length > (timeInfo ? 9 : 15) ? '...' + filename.slice(-(timeInfo ? 9 : 15)) : filename;
                return truncatedFilename + timeInfo;
              })()}
                    </div>
                  </div>)}
              </div>
              {mediaValue && mediaValue?.length > 0 && <div className="flex flex-row items-center w-full box-border p-1 border-1 border-white/20 border-solid rounded-sm overflow-hidden">
                  {!isFullscreen && mediaValue?.length > 1 && <button className="flex justify-start cursor-pointer disabled:opacity-20 disabled:cursor-not-allowed" onClick={() => setCurrentMediaIndex(currentMediaIndex - 1)} disabled={currentMediaIndex === 0}>
                      <CaretLeftIcon size={30} />
                    </button>}
                  {mediaValue?.length > 0 && <MediaDisplay mediaItem={mediaValue[currentMediaIndex]} isEventModal={true} updateMediaContext={updateMediaContext} isTitleSlide={isTitleSlide} containerHeight={460} containerWidth={360} caseId={caseId} />}
                  {mediaValue?.length > 1 && <button className="flex justify-end cursor-pointer disabled:opacity-20 disabled:cursor-not-allowed" onClick={() => setCurrentMediaIndex(currentMediaIndex + 1)} disabled={currentMediaIndex === mediaValue.length - 1}>
                      <CaretRightIcon size={30} />
                    </button>}
                </div>}
            </div>}
        </div>}
    </div>;
};
UnifiedSources.displayName = 'UnifiedSources';
export default UnifiedSources;